import { queryOptions } from '@tanstack/react-query';
import {
  activityFeedSchema,
  activityQuerySchema,
  ACTIVITY_TEMPLATES,
  ACTIVITY_VIA_TEMPLATE,
  getInterfaceLanguageCatalogue,
  workspaceActivityFeedSchema,
  workspaceActivityQuerySchema,
  type ActivityEntry,
  type ActivityFeed,
  type ActivityQuery,
  type ApiError,
  type InterfaceLanguageCatalogueResponse,
  type WorkspaceActivityFeed,
  type WorkspaceActivityQuery,
} from '@molt/shared';
import type { UseQueryOptions } from '@tanstack/react-query';

import { get } from './api';
import { queryKeys } from './query-keys';

export type ActivityFilter = 'all' | 'comments';

export function getCardActivity(
  cardId: string,
  query: Partial<ActivityQuery> = {},
): Promise<ActivityFeed> {
  return getActivity(`/cards/${encodeURIComponent(cardId)}/activity`, query);
}

export function getBoardActivity(
  boardId: string,
  query: Partial<ActivityQuery> = {},
): Promise<ActivityFeed> {
  return getActivity(`/boards/${encodeURIComponent(boardId)}/activity`, query);
}

export function getWorkspaceActivity(
  workspaceId: string,
  query: Partial<WorkspaceActivityQuery> = {},
): Promise<WorkspaceActivityFeed> {
  const params = workspaceActivityQuerySchema.partial().parse(query);
  const search = new URLSearchParams();
  if (params.filter !== undefined) search.set('filter', params.filter);
  if (params.cursor !== undefined) search.set('cursor', params.cursor);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.memberId !== undefined) search.set('memberId', params.memberId);
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`;
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/activity${suffix}`,
    workspaceActivityFeedSchema,
  );
}

function getActivity(
  path: string,
  query: Partial<ActivityQuery> = {},
): Promise<ActivityFeed> {
  const params = activityQuerySchema.partial().parse(query);
  const search = new URLSearchParams();
  if (params.filter !== undefined) search.set('filter', params.filter);
  if (params.cursor !== undefined) search.set('cursor', params.cursor);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`;
  return get(`${path}${suffix}`, activityFeedSchema);
}

export function cardActivityQueryOptions(
  cardId: string,
  filter: ActivityFilter,
  limit?: number,
) {
  return queryOptions({
    queryKey: queryKeys.cards.activity(cardId, filter),
    queryFn: () =>
      getCardActivity(cardId, {
        filter,
        ...(limit === undefined ? {} : { limit }),
      }),
  });
}

export function boardActivityQueryOptions(
  boardId: string,
  filter: ActivityFilter,
  limit?: number,
) {
  return queryOptions({
    queryKey: queryKeys.boards.activity(boardId, filter),
    queryFn: () =>
      getBoardActivity(boardId, {
        filter,
        ...(limit === undefined ? {} : { limit }),
      }),
  });
}

export function workspaceActivityQueryOptions(
  workspaceId: string,
  filter: ActivityFilter,
  memberId?: string | null,
  limit?: number,
): UseQueryOptions<WorkspaceActivityFeed, ApiError> {
  return {
    queryKey: queryKeys.workspaces.activity(workspaceId, filter, memberId),
    queryFn: () =>
      getWorkspaceActivity(workspaceId, {
        filter,
        ...(memberId === null || memberId === undefined ? {} : { memberId }),
        ...(limit === undefined ? {} : { limit }),
      }),
  };
}

export function mergeActivityFeeds(
  current: ActivityFeed | undefined,
  next: ActivityFeed,
): ActivityFeed | undefined {
  if (current === undefined) return next;
  return {
    entries: [...current.entries, ...next.entries],
    nextCursor: next.nextCursor,
  };
}

export function mergeWorkspaceActivityFeeds(
  current: WorkspaceActivityFeed | undefined,
  next: WorkspaceActivityFeed,
): WorkspaceActivityFeed | undefined {
  if (current === undefined) return next;
  return {
    entries: [...current.entries, ...next.entries],
    nextCursor: next.nextCursor,
  };
}

/**
 * Mirrors `CommentTranslate` (`lib/comments.ts:37-41`). Required and first —
 * an optional translator is how a string stays English forever
 * (`FE-80.1` brief §6.4). `activitySentence`'s `catalogue = null` idiom is
 * deliberately NOT copied: those are `activityTemplates`, which the server
 * serves; these are `messages` keys, which it does not.
 */
export type ActivityTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/**
 * ⚠ **Both literals are keyed, or the function ships half-translated.** The
 * deleted-account branch and the empty-name branch are two ends of one name
 * chain; translating one and freezing the other renders a translated
 * deleted-account name beside an English anonymous-member name in the same
 * feed — the half-state the brief forbids. `primitives.avatar.anonymousMember` is
 * keyed here and `Avatar.tsx`'s `ANONYMOUS_MEMBER_NAME` constant is not — see
 * the `FE-80.1` brief §5.3: `profileName` keeps one language for all ~45 of
 * its callers, and this function keeps one language for all three of its own.
 */
export function activityActorName(t: ActivityTranslate, entry: ActivityEntry): string {
  if (entry.actor !== null) {
    const displayName = entry.actor.displayName.trim();
    if (displayName !== '') return displayName;
    const username = entry.actor.username.trim();
    return username === '' ? t('primitives.avatar.anonymousMember') : `@${username}`;
  }
  const automationName = entry.actorAutomationName?.trim();
  return automationName === undefined || automationName === ''
    ? t('primitives.avatar.deletedAccount')
    : automationName;
}

export function activitySentence(
  entry: ActivityEntry,
  catalogue: InterfaceLanguageCatalogueResponse | null = null,
): string {
  const templates = catalogue?.activityTemplates ?? fallbackActivityTemplates();
  const sentence = renderActivityTemplate(
    templates[entry.templateKey] ?? ACTIVITY_TEMPLATES[entry.templateKey],
    entry,
  );
  const automationName = entry.actorAutomationName?.trim();
  return entry.actor === null || automationName === undefined || automationName === ''
    ? sentence
    : ACTIVITY_VIA_TEMPLATE.replace('{sentence}', sentence).replace(
        '{tool}',
        automationName,
      );
}

function fallbackActivityTemplates(): InterfaceLanguageCatalogueResponse['activityTemplates'] {
  return getInterfaceLanguageCatalogue('en').activityTemplates;
}

function renderActivityTemplate(template: string, entry: ActivityEntry): string {
  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (_match, slot: string) => {
    const token = entry.tokens[slot];
    if (token === undefined) {
      throw new Error(
        `activity template "${entry.templateKey}" needs a token for {${slot}}`,
      );
    }
    return token.text;
  });
}
